import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import "./ManualAdjustments.styles.scss";

// Component Imports
import { Loading } from "@/layout";
import { DropdownInput } from "@/components/Inputs";
import { AGTable, AlertMessage } from "@/components";

// Context/Hook Imports
import { usePortfolioAndAccountSelection } from "@/utils/hooks/usePortfolioAndAccountSelection/usePortfolioAndAccountSelection";
import { useAppContext } from "@/context/context";

// Package Imports
import { v4 as uuidv4 } from "uuid";
import {
  ColDef,
  GetContextMenuItemsParams,
} from "ag-grid-community";
import {
  withCopyMenuItems,
  type ContextMenuItem,
} from "@pulse-ui/shared-components";

// MUI Imports
import { Button, Divider, IconButton, SvgIcon, Tooltip } from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

// SVG Imports
import LogoSVG from "@/svgs/pulse_prime_logo.svg?react";
import { IAlertMessage } from "@pulse-ui/types/components/AlertMessage";

// Utils/Formatting Imports
import {
  deactivateManualAdjustment,
  fetchActiveManualAdjustments,
  getOrgMetadata,
  getOrgs,
  setAccountById,
  setOrgMetadata,
  setPortfolioById,
} from "@/utils/promises";
import { formatIsoDateToLocale } from "@pulse-ui/formatting";
import { useAlert } from "@/utils/hooks/useAlert/useAlert";

// Type Imports
import { ContextStateType } from "@/context/context.types";
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";
import {
  IAccountMetadata,
  IPortfolioMetadata,
} from "@pulse-ui/codegen/generated_ts/org_metadata";
import {
  ManualAdjustmentDetailsModal,
  ManualAdjustmentsUploadModal,
} from "@/components/Modals/ManualAdjustments";
import { IAGManualAdjustmentsDataRow } from "@pulse-ui/types/components/AGTable";
import DeleteModal from "@/components/Modals/DeleteModal/DeleteModal";

const ManualAdjustments = () => {
  // State - START -------------------------------------
  const ALL_ACCOUNTS = "all-accounts";
  const BALANCE_UPDATE = "balance_update";
  const POSITION_UPDATE = "position_update";
  const location_origin = window.location.origin;
  const navigate = useNavigate();

  // Query Params States
  const [queryParams, setQueryParams] = useSearchParams({
    portfolio_id: "",
    dropcopy_feed_type: "",
  });
  const portfolio_id_param = queryParams.get("portfolio_id");
  const dropcopy_feed_type_param = queryParams.get("dropcopy_feed_type");

  // General States
  const gridRef = useRef();
  const [loading, setLoading] = useState<boolean>(true);
  const [rowData, setRowData] = useState<any[]>([]);
  const [initialRender, setInitialRender] = useState<boolean>(true);
  const feedTypeOptions: ISelectOption[] = [
    { label: "Balance Update", value: BALANCE_UPDATE },
    { label: "Position Update", value: POSITION_UPDATE },
  ];

  // Error Handling / Alert Message States
  const { alerts, pushAlert, closeAlert, clearAlerts } = useAlert();

  // Context States
  const {
    context_state,
    set_portfolio_by_id,
    set_account_by_id,
    update_org_metadata,
  } = useAppContext();

  // Local Storage
  const stringified_local_metadata: string | null =
    localStorage.getItem("pulse-ctx");
  const local_metadata: ContextStateType | null = stringified_local_metadata
    ? JSON.parse(stringified_local_metadata)
    : null;

  // Modal States
  const [manualAdjustmentsUploadModalOpen, setManualAdjustmentsModalOpen] =
    useState<boolean>(false);
  const [deleteModalStatus, setDeleteModalStatus] = useState<{
    open: boolean;
    item_id: string | null;
    account_id: string | null;
    item_name: string | null;
  }>({
    open: false,
    item_id: null,
    account_id: null,
    item_name: null,
  });
  const [
    manualAdjustmentDetailsModalStatus,
    setManualAdjustmentDetailsModalStatus,
  ] = useState<{
    open: boolean;
    original_row: any;
  }>({
    open: false,
    original_row: null,
  });
  // State - END ---------------------------------------

  // Methods - START -----------------------------------
  /**
   * Function to handle navigation.
   * This function updates the query parameters for portfolio ID and dropcopy feed type.
   * It also clears the row data.
   *
   * @param {string | null} portfolio_id - The portfolio ID to set in the query parameters.
   * @param {string | null} dropcopy_feed_type - The dropcopy feed type to set in the query parameters.
   */
  const handleNavigate = (
    portfolio_id: string | null,
    dropcopy_feed_type: string | null
  ) => {
    setRowData([]);
    setQueryParams((prev: URLSearchParams) => {
      prev.set("portfolio_id", portfolio_id || "");
      prev.set("dropcopy_feed_type", dropcopy_feed_type || "");

      return prev;
    });
  };

  /**
   * Function to map portfolios to select options.
   * This function converts an array of portfolio metadata to an array of select options.
   *
   * @param {IPortfolioMetadata[]} portfolios - The array of portfolio metadata to map.
   * @returns {ISelectOption[]} - The array of select options.
   */
  const mapIPortfoliosToSelectOptions = (
    portfolios: IPortfolioMetadata[]
  ): ISelectOption[] => {
    if (!portfolios) {
      return [];
    } else {
      let options: ISelectOption[] = [];

      for (let portfolio of portfolios) {
        options.push({
          label: portfolio.display_name,
          value: portfolio.portfolio_id,
        });
      }

      return options;
    }
  };

  /**
   * Function to validate the feed type.
   * This function checks if the provided feed type is either POSITION_UPDATE or BALANCE_UPDATE.
   * If the feed type is valid, it returns the feed type; otherwise, it defaults to BALANCE_UPDATE.
   *
   * @param {string | null} feed_type - The feed type to validate.
   * @returns {string} - The validated feed type.
   */
  const validateFeedType = (feed_type: string | null): string => {
    return (feed_type && feed_type === POSITION_UPDATE) ||
      feed_type === BALANCE_UPDATE
      ? feed_type
      : BALANCE_UPDATE;
  };

  /**
   * Function to handle changes in dropdown selections.
   * This function updates the state based on the selected dropdown value.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} e - The change event from the dropdown.
   */
  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Clear row data if it exists
    if (rowData.length > 0) setRowData([]);
    // Clear alert messages if they exist
    if (alerts.length > 0) clearAlerts();

    // Handle portfolio selection change
    if (name === "portfolio-select") {
      value === "" ? set_portfolio_by_id(null) : set_portfolio_by_id(value);
      setQueryParams((prev: URLSearchParams) => {
        prev.set("portfolio_id", value);
        return prev;
      });
      return;
    }

    // Handle dropcopy feed type selection change
    if (name === "dropcopy-feed-type-select") {
      setQueryParams((prev: URLSearchParams) => {
        prev.set("dropcopy_feed_type", value);
        return prev;
      });
      return;
    }
  };

  /**
   * Gets the display name for an account given its portfolio and account IDs.
   *
   * @param {string} portfolio_id - The ID of the portfolio containing the account
   * @param {string} account_id - The ID of the account to look up
   * @returns {string} The account's display name or "-" if not found
   */
  const getAccountDisplayName = (
    portfolio_id: string,
    account_id: string
  ): string => {
    // Early returns for invalid inputs
    if (!portfolio_id || !account_id) {
      return "-";
    }

    // Destructure for cleaner access and null checking
    const { org_metadata } = context_state;
    if (!org_metadata?.portfolios?.length) {
      return "-";
    }

    // Find portfolio and account in one pass
    const portfolio = org_metadata.portfolios.find(
      (p: IPortfolioMetadata) => p.portfolio_id === portfolio_id
    );

    if (!portfolio?.accounts?.length) {
      return "-";
    }

    const account = portfolio.accounts.find(
      (a: IAccountMetadata) => a.account_id === account_id
    );

    return account?.display_name ?? "-";
  };

  /**
   * Function to parse manual adjustments to row data.
   * This function maps the manual adjustments to the format required by the AG Grid.
   *
   * @param {any[]} manual_adjustments - The array of manual adjustments to parse.
   * @returns {IAGManualAdjustmentsDataRow[]} - The parsed row data for the AG Grid.
   */
  const parse_manual_adjustment_to_row_data = (
    manual_adjustment: any
  ): IAGManualAdjustmentsDataRow => {
    return {
      row_id: manual_adjustment.metadata.adjustment_id,
      account_id: manual_adjustment.metadata.account_id,
      account_name: getAccountDisplayName(
        portfolio_id_param || "",
        manual_adjustment.metadata.account_id
      ),
      description: manual_adjustment.metadata.description,
      feed_type: manual_adjustment.metadata.feed_type,
      timestamp: formatIsoDateToLocale(manual_adjustment.metadata.timestamp),
      original_row: manual_adjustment,
    };
  };

  /**
   * Function to generate request promises for fetching manual adjustments.
   * Generates an array of promises to fetch manual adjustments for each account in a portfolio.
   *
   * @param {string} location_origin - The API base URL
   * @param {string} portfolio_id_param - The portfolio ID to fetch accounts from
   * @param {string} dropcopy_feed_type_param - The type of feed to fetch (balance/position)
   * @returns {Promise<any>[] | undefined} Array of promises for fetching adjustments, or undefined if no portfolio/accounts
   */
  const generateRequestPromises = (
    location_origin: string,
    portfolio_id_param: string,
    dropcopy_feed_type_param: string
  ): Promise<any>[] | undefined => {
    // Early return if no org metadata
    if (!context_state.org_metadata?.portfolios) {
      console.warn("No portfolios found in org metadata");
      return undefined;
    }

    // Find the target portfolio
    const portfolio = context_state.org_metadata.portfolios.find(
      (p: IPortfolioMetadata) => p.portfolio_id === portfolio_id_param
    );

    // Early return if no portfolio or no accounts
    if (!portfolio?.accounts?.length) {
      console.warn(`No accounts found for portfolio ID: ${portfolio_id_param}`);
      return undefined;
    }

    // Generate promise for each account
    return portfolio.accounts.map((account: IAccountMetadata) =>
      fetchActiveManualAdjustments(
        location_origin,
        account.account_id,
        dropcopy_feed_type_param
      )
        .then((response) => response.data)
        .catch((error) => {
          pushAlert({
            id: uuidv4(),
            message: error?.error || "Error fetching manual adjustments",
            status: error?.status || 500,
          });
          return null;
        })
    );
  };

  /**
   * Function to handle fetching active manual adjustments.
   * This function performs an asynchronous operation to fetch active manual adjustments based on the provided parameters.
   *
   * @param {string} location_origin - The origin location for the API request.
   * @param {string} portfolio_id_param - The portfolio ID parameter.
   * @param {string} dropcopy_feed_type_param - The dropcopy feed type parameter.
   */
  const handleFetchActiveManualAdjustments = async (
    location_origin: string,
    portfolio_id_param: string,
    dropcopy_feed_type_param: string
  ): Promise<{ status: number; message: any }> => {
    return new Promise((resolve, _) => {
      let manual_adjustments_arr: any[] = [];
      let promises: any = generateRequestPromises(
        location_origin,
        portfolio_id_param,
        dropcopy_feed_type_param
      );

      Promise.all(promises)
        .then((res) => {
          manual_adjustments_arr = res.filter((item) => item !== null);

          resolve({
            status: 200,
            message: null,
          });
        })
        .finally(() => {
          const flattened_manual_adjustments_arr =
            manual_adjustments_arr.flat();
          if (flattened_manual_adjustments_arr.length < 1) {
            setRowData([]);
          } else {
            const parsed_row_data = flattened_manual_adjustments_arr.map(
              (manual_adjustments) => {
                return parse_manual_adjustment_to_row_data(manual_adjustments);
              }
            );

            setRowData(parsed_row_data);
          }
        });
    });
  };

  /**
   * Function to handle deactivating a manual adjustment.
   * This function performs an asynchronous operation to deactivate a manual adjustment based on the provided adjustment ID.
   *
   * @param {string} location_origin - The origin location for the API request.
   * @param {string} adjustment_id - The ID of the manual adjustment to deactivate.
   */
  const handleDeactivateManualAdjustment = async (
    location_origin: string,
    adjustment_id: string,
    account_id: string
  ) => {
    setLoading(true);

    try {
      // Deactivate manual adjustment using the adjustment_id
      const deactivate_manual_adjustment_res = await deactivateManualAdjustment(
        location_origin,
        adjustment_id,
        account_id
      );

      pushAlert({
        id: uuidv4(),
        message: "Manual adjustment deactivated successfully.",
        status: deactivate_manual_adjustment_res.status,
      });

      handleFetchActiveManualAdjustments(
        location_origin,
        portfolio_id_param || "",
        dropcopy_feed_type_param || BALANCE_UPDATE
      );

      setDeleteModalStatus({
        open: false,
        item_id: null,
        account_id: null,
        item_name: null,
      });
    } catch (error_res: any) {
      // Push an alert message if an error occurs
      pushAlert({
        id: uuidv4(),
        message: error_res.error,
        status: error_res?.status || 500,
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Function to handle the initial data load for the blotter.
   * This function performs several asynchronous operations to fetch and set up the initial data.
   */
  const handleInitialDataLoad = async () => {
    try {
      setLoading(true);

      // Fetch the organization details using the location origin
      const get_orgs_res = await getOrgs(location_origin);
      // Fetch the organization metadata using the organization ID and location origin
      const get_org_metadata_res = await getOrgMetadata(
        get_orgs_res.data.org_id,
        location_origin
      );
      // Set the organization metadata in the context
      await setOrgMetadata(get_org_metadata_res.data, update_org_metadata);

      // Use the portfolio and account selection utility to get the portfolio and account IDs
      const { portfolio_id_res, account_id_res } =
        await usePortfolioAndAccountSelection(
          ALL_ACCOUNTS,
          local_metadata,
          get_org_metadata_res.data,
          true
        );
      // If no portfolio ID is returned, set account and portfolio to null and navigate to unauthorized page
      if (!portfolio_id_res) {
        set_account_by_id(null);
        set_portfolio_by_id(null);
        navigate("/unauthorized", { replace: true });
      }

      // Validate the feed type
      const validated_feed_type = validateFeedType(dropcopy_feed_type_param);

      // Navigate to the appropriate account and feed type
      handleNavigate(portfolio_id_res || "", validated_feed_type);

      // Set the selected portfolio by ID in context
      await setPortfolioById(portfolio_id_res || null, set_portfolio_by_id);
      // Set the selected account by ID in context
      await setAccountById(account_id_res, set_account_by_id);

      // If an account ID parameter is provided, fetch the manual adjustments data
      if (portfolio_id_param) {
        handleFetchActiveManualAdjustments(
          location_origin,
          portfolio_id_param,
          validated_feed_type
        );
      }
    } catch (error_res: any) {
      // Push an alert message if an error occurs
      pushAlert({
        id: uuidv4(),
        message: error_res.error,
        status: error_res?.status || 500,
      });
    } finally {
      setLoading(false);
      setInitialRender(false);
    }
  };

  // Column Definitions: Defines & controls grid columns.
  const colDefs = useMemo<ColDef<IAGManualAdjustmentsDataRow>[]>(
    () => [
      {
        headerName: "Account ID",
        field: "account_id",
        cellClass: "ag-center-aligned-cell",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Account Name",
        field: "account_name",
        cellClass: "ag-center-aligned-cell",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Description",
        field: "description",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Feed Type",
        field: "feed_type",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Timestamp",
        field: "timestamp",
        cellClass: "ag-center-aligned-cell",
      },
    ],
    []
  );

  const defaultColDef: ColDef = {
    flex: 1,
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): ContextMenuItem[] => {
      const result: ContextMenuItem[] = [
        {
          name: "Show Adjustment",
          action: () => {
            setManualAdjustmentDetailsModalStatus({
              open: true,
              original_row: params.node?.data.original_row,
            });
          },
          cssClasses: ["pulse-color--background-100"],
        },
        {
          name: "Deactivate Adjustment",
          action: () => {
            setDeleteModalStatus({
              open: true,
              item_id: params.node?.data.row_id,
              account_id: params.node?.data.account_id,
              item_name: params.node?.data.description,
            });
          },
          cssClasses: ["pulse-color--error-400"],
        },
      ];
      return withCopyMenuItems(result);
    },
    [window]
  );
  // Methods - END -------------------------------------

  // useEffects - START --------------------------------
  /**
   * useEffect hook to handle the initial data load for the Manual Adjustments page.
   * This effect runs only once when the component is mounted.
   * It performs several asynchronous operations to fetch and set up the initial data.
   *
   * @see handleInitialDataLoad for the detailed implementation of the initial data load process.
   */
  useEffect(() => {
    handleInitialDataLoad();
  }, []);

  /**
   * useEffect hook to handle fetching active manual adjustments when account_id_param or dropcopy_feed_type_param changes.
   *
   * This effect runs when either account_id_param or dropcopy_feed_type_param changes, except for the initial render.
   * It sets the loading state to true, updates the account by ID, validates the feed type, and fetches active manual adjustments.
   *
   * @param {string} account_id_param - The account ID parameter.
   * @param {string} dropcopy_feed_type_param - The dropcopy feed type parameter.
   */
  useEffect(() => {
    if (!initialRender) {
      setLoading(true);

      portfolio_id_param === ""
        ? set_portfolio_by_id(null)
        : set_portfolio_by_id(portfolio_id_param);

      if (portfolio_id_param) {
        const validated_feed_type = validateFeedType(dropcopy_feed_type_param);

        handleFetchActiveManualAdjustments(
          location_origin,
          portfolio_id_param,
          validated_feed_type
        );
      }
    }

    setLoading(false);
  }, [portfolio_id_param, dropcopy_feed_type_param]);
  // useEffects - END ----------------------------------

  return (
    <div className="pulse-sidenav-adjusted-container manual-adjustments-container pulse-bg-color--background-900">
      <SvgIcon
        sx={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
        children={<LogoSVG className="pulse-color--background-800" />}
      />
      {alerts.map((alert: IAlertMessage) => {
        return (
          <div className="pulse-mb-1" style={{ width: "100%" }} key={uuidv4()}>
            <AlertMessage
              id={alert.id}
              message={alert.message}
              status={alert.status}
              closeAlertMessage={closeAlert}
            />
          </div>
        );
      })}
      <div className="pulse-row manual-adjustments-header-row manual-adjustments-position-relative">
        <p className="pulse-typography-headline-6 pulse-color--background-50 pulse-col-md-2 pulse-col-lg-2 pulse-col-xl-2">
          Manual Adjustments
        </p>

        <div className="manual-adjustments-dropdown-inputs-container pulse-col-md-4 pulse-col-lg-4 pulse-col-xl-4 pulse-mb-4">
          <DropdownInput
            name="portfolio-select"
            value={context_state.selected_portfolio?.portfolio_id || ""}
            label="Portfolio"
            onChange={handleDropdownChange}
            disabled={context_state.org_metadata.portfolios.length < 1}
            options={mapIPortfoliosToSelectOptions(
              context_state.org_metadata.portfolios
            )}
            formInput={false}
            containerClassNames="pulse-mr-1"
          />

          <DropdownInput
            name="dropcopy-feed-type-select"
            value={dropcopy_feed_type_param || BALANCE_UPDATE}
            label="Feed Type"
            onChange={handleDropdownChange}
            disabled={context_state.selected_account === null}
            options={feedTypeOptions}
            formInput={false}
            containerClassNames=""
          />
        </div>
      </div>
      <div className="pulse-row manual-adjustments-header-subrow manual-adjustments-position-relative pulse-mb-1">
        <a href="/downloads/ManualAdjustmentUploader-template.csv" download>
          <Button
            className="download-template-button"
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              backgroundColor: "bg.700",
              color: "bg.100",
              typography: "fontSizeSmall",
              "&:hover": {
                backgroundColor: "bg.main",
              },
              "&:disabled": {
                backgroundColor: "bg.800",
                color: "bg.300",
              },
            }}
          >
            Download Template
          </Button>
        </a>

        <Button
          variant="contained"
          onClick={() => setManualAdjustmentsModalOpen(true)}
          startIcon={<UploadIcon />}
          disabled={!context_state.selected_account}
          sx={{
            backgroundColor: "success.700",
            color: "success.100",
            typography: "fontSizeSmall",
            "&:hover": {
              backgroundColor: "success.main",
            },
            "&:disabled": {
              backgroundColor: "success.800",
              color: "bg.300",
            },
          }}
        >
          Upload
        </Button>

        <Tooltip
          placement="bottom-start"
          title={
            <>
              <p className="pulse-typography-subtitle-2 pulse-mb-2 pulse-color--background-900">
                Template
              </p>

              <p className="pulse-typography-body-1 pulse-mb-1 pulse-color--background-900">
                Download the template as a reference for creating your CSV file.
              </p>

              <Divider sx={{ marginBottom: "1.5rem" }} />

              <p className="pulse-typography-subtitle-2 pulse-mb-2 pulse-color--background-900">
                Upload Process
              </p>

              <p className="pulse-typography-body-1 pulse-mb-1 pulse-color--background-900">
                1. Click the{" "}
                <span className="tooltip-span pulse-color--background-900">
                  UPLOAD
                </span>{" "}
                button.
              </p>
              <p className="pulse-typography-body-1 pulse-mb-1 pulse-color--background-900">
                2. Select the file you want to upload.
              </p>
              <p className="pulse-typography-body-1 pulse-mb-1 pulse-color--background-900">
                3. Click on the{" "}
                <span className="tooltip-span pulse-color--background-900">
                  Submit
                </span>{" "}
                button to upload the manual adjustments.
              </p>

              <Divider sx={{ marginBottom: "1.5rem" }} />

              <p className="pulse-typography-subtitle-2 pulse-mb-2 pulse-color--background-900">
                Formatting Details
              </p>

              <p className="pulse-typography-body-1 pulse-mb-1 pulse-color--background-900">
                To generate a valid CSV file, do not modify the column names.
              </p>
              <p className="pulse-typography-body-1 pulse-color--background-900">
                Fields that represent lists of structured values, such as{" "}
                <span className="tooltip-span pulse-color--background-900">
                  parties
                </span>{" "}
                or{" "}
                <span className="tooltip-span pulse-color--background-900">
                  amts
                </span>
                , should be entered as a valid JSON string.
              </p>
              <p className="pulse-typography-body-1 pulse-mb-2 pulse-color--background-900">
                For example:{" "}
                <span className="tooltip-span pulse-color--background-900">{`[{"party_role":"exchange","party_id":"BINF"}]`}</span>
              </p>
            </>
          }
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: "#b6bdce",
                width: "100%",
                maxWidth: "none",
              },
            },
            popper: {
              sx: {
                backgroundColor: "#b6bdce",
                width: "50%",
              },
            },
          }}
        >
          <IconButton>
            <HelpOutlineOutlinedIcon className="pulse-color--background-50" />
          </IconButton>
        </Tooltip>
      </div>
      <AGTable
        gridRef={gridRef}
        rowData={rowData}
        colDefs={colDefs}
        defaultColDef={defaultColDef}
        groupDisplayType="singleColumn"
        sideBar={false}
        animateRows
        getContextMenuItems={getContextMenuItems}
      />
      {manualAdjustmentsUploadModalOpen && (
        <ManualAdjustmentsUploadModal
          handleClose={() => setManualAdjustmentsModalOpen(false)}
          setLoading={setLoading}
          handleFetchActiveManualAdjustments={
            handleFetchActiveManualAdjustments
          }
          portfolio_id_param={portfolio_id_param || ""}
          dropcopy_feed_type_param={dropcopy_feed_type_param || BALANCE_UPDATE}
        />
      )}
      <ManualAdjustmentDetailsModal
        open={manualAdjustmentDetailsModalStatus.open}
        originalRow={manualAdjustmentDetailsModalStatus.original_row}
        handleClose={() =>
          setManualAdjustmentDetailsModalStatus({
            open: false,
            original_row: null,
          })
        }
      />
      {deleteModalStatus.open && (
        <DeleteModal
          open
          item_name={deleteModalStatus.item_name}
          handleClose={() =>
            setDeleteModalStatus({
              open: false,
              item_id: null,
              account_id: null,
              item_name: null,
            })
          }
          handleSubmit={() =>
            handleDeactivateManualAdjustment(
              location_origin,
              deleteModalStatus.item_id || "",
              deleteModalStatus.account_id || ""
            )
          }
        />
      )}
      {loading && <Loading />}
    </div>
  );
};

export default ManualAdjustments;
